import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockOpen as UnlockedIcon,
  Lock as LockedIcon,
  ViewList as ViewListIcon,
  AccountTree as AccountTreeIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  FileCopy as FileCopyIcon,
  CloudUpload as CloudUploadIcon,
  Inbox as InboxIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { getAssetTypeLabel, truncateGuid } from '../utils/assetUtils';
import QuestEditDialog from './QuestEditDialog';
import TaskEditDialog from './TaskEditDialog';
import QuestLineEditDialog from './QuestLineEditDialog';
import QuestLineTreeView from './QuestLineTreeView';

export default function QuestManager() {
  const queryClient = useQueryClient();
  const [selectedQuestLine, setSelectedQuestLine] = useState(null);
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuestLineType, setSelectedQuestLineType] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'tree'
  const [questLineEditDialog, setQuestLineEditDialog] = useState({ open: false, questLine: null });
  const [questEditDialog, setQuestEditDialog] = useState({ open: false, quest: null });
  const [taskEditDialog, setTaskEditDialog] = useState({ open: false, task: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingQuestLine, setEditingQuestLine] = useState(false);
  const [editingQuest, setEditingQuest] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [questLineFormData, setQuestLineFormData] = useState(null);
  const [questFormData, setQuestFormData] = useState(null);
  const [savingQuestLine, setSavingQuestLine] = useState(false);
  const [savingQuest, setSavingQuest] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [clearExisting, setClearExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Staging feature state
  const [selectedForExport, setSelectedForExport] = useState(new Set());
  const [stagedFilesDialogOpen, setStagedFilesDialogOpen] = useState(false);
  const [selectedStagedFile, setSelectedStagedFile] = useState(null);
  const [stagedFilePreview, setStagedFilePreview] = useState(null);
  const [stagingInProgress, setStagingInProgress] = useState(false);
  const [importingStagedFile, setImportingStagedFile] = useState(false);
  
  // Get environment context
  const { isDevelopment } = useEnvironment();

  // Fetch all quest lines
  const { data: questLines = [], isLoading: questLinesLoading } = useQuery({
    queryKey: ['questLines'],
    queryFn: async () => {
      const response = await api.get('/quests/quest-lines');
      return response.data;
    },
    staleTime: 2 * 60 * 1000
  });

  // Fetch all quests for comprehensive search
  const { data: allQuests = [], isLoading: allQuestsLoading } = useQuery({
    queryKey: ['allQuests'],
    queryFn: async () => {
      const questsList = [];
      for (const questLine of questLines) {
        try {
          const response = await api.get(`/quests/quest-lines/${questLine.id}/quests`);
          const quests = response.data.map(quest => ({ ...quest, questLineId: questLine.id, questLine }));
          questsList.push(...quests);
        } catch (error) {
          console.error(`Failed to fetch quests for quest line ${questLine.id}:`, error);
        }
      }
      return questsList;
    },
    enabled: questLines.length > 0,
    staleTime: 2 * 60 * 1000
  });

  // Fetch all tasks for comprehensive search
  const { data: allTasks = [], isLoading: allTasksLoading } = useQuery({
    queryKey: ['allTasks'],
    queryFn: async () => {
      const tasksList = [];
      for (const quest of allQuests) {
        try {
          const response = await api.get(`/quests/quests/${quest.id}/tasks`);
          const tasks = response.data.map(task => ({ 
            ...task, 
            questId: quest.id, 
            quest,
            questLineId: quest.questLineId,
            questLine: quest.questLine
          }));
          tasksList.push(...tasks);
        } catch (error) {
          console.error(`Failed to fetch tasks for quest ${quest.id}:`, error);
        }
      }
      return tasksList;
    },
    enabled: allQuests.length > 0,
    staleTime: 2 * 60 * 1000
  });

  // Fetch all assets for lookups
  const { data: assets = [] } = useQuery({
    queryKey: ['allAssets'],
    queryFn: async () => {
      const response = await api.get('/inventories/items');
      return response.data;
    },
    staleTime: 5 * 60 * 1000
  });

  // Build asset map for fast lookup
  const assetMap = useMemo(() => {
    const map = new Map();
    assets.forEach(asset => {
      map.set(asset.id, asset);
    });
    return map;
  }, [assets]);

  // Fetch staged files (for production environment)
  const { data: stagedFiles = { files: [], count: 0 }, refetch: refetchStagedFiles } = useQuery({
    queryKey: ['stagedFiles'],
    queryFn: async () => {
      const response = await api.get('/quests/staged');
      return response.data;
    },
    staleTime: 30 * 1000, // Refresh every 30 seconds
    refetchInterval: 60 * 1000 // Auto-refresh every minute
  });

  // Fetch quests for selected quest line
  const { data: quests = [], isLoading: questsLoading } = useQuery({
    queryKey: ['quests', selectedQuestLine?.id],
    queryFn: async () => {
      const response = await api.get(`/quests/quest-lines/${selectedQuestLine.id}/quests`);
      return response.data;
    },
    enabled: !!selectedQuestLine,
    staleTime: 2 * 60 * 1000,
    onSuccess: (data) => {
      // Auto-select first quest when quest line is selected
      if (data && data.length > 0 && !selectedQuest) {
        setSelectedQuest(data[0]);
      }
      // Initialize quest line form data when quest line is selected
      if (selectedQuestLine && !questLineFormData) {
        setQuestLineFormData({
          displayName: selectedQuestLine.displayName || '',
          description: selectedQuestLine.description || '',
          questLineType: selectedQuestLine.questLineType || 'story',
          requirements: parseRequirements(selectedQuestLine.requirements),
          isActive: selectedQuestLine.isActive === 1 || selectedQuestLine.isActive === true ? 1 : 0,
          sortOrder: selectedQuestLine.sortOrder || 0,
          targetPassTier: selectedQuestLine.targetPassTier || '',
          targetDay: selectedQuestLine.targetDay || '',
          automaticActivation: selectedQuestLine.automaticActivation === 1 || selectedQuestLine.automaticActivation === true ? 1 : 0,
          conversationIds: selectedQuestLine.conversationIds || {
            offerConversationId: null,
            startConversationId: null,
            endConversationId: null
          }
        });
      }
    }
  });

  // Initialize quest form data when quest is selected
  useEffect(() => {
    if (selectedQuest) {
      setQuestFormData({
        title: selectedQuest.title || '',
        description: selectedQuest.description || '',
        questOrder: selectedQuest.questOrder || 0,
        itemsToGive: parseJSON(selectedQuest.itemsToGive),
        rewards: parseJSON(selectedQuest.rewards),
        isActive: selectedQuest.isActive === 1 || selectedQuest.isActive === true ? 1 : 0,
        conversationIds: selectedQuest.conversationIds || {
          startConversationId: null,
          endConversationId: null
        }
      });
    } else {
      setQuestFormData(null);
    }
  }, [selectedQuest]);

  // Reset quest line form data when quest line changes
  useEffect(() => {
    if (selectedQuestLine) {
      setQuestLineFormData({
        displayName: selectedQuestLine.displayName || '',
        description: selectedQuestLine.description || '',
        questLineType: selectedQuestLine.questLineType || 'story',
        requirements: parseRequirements(selectedQuestLine.requirements),
        isActive: selectedQuestLine.isActive === 1 || selectedQuestLine.isActive === true ? 1 : 0,
        sortOrder: selectedQuestLine.sortOrder || 0,
        targetPassTier: selectedQuestLine.targetPassTier || '',
        targetDay: selectedQuestLine.targetDay || '',
        automaticActivation: selectedQuestLine.automaticActivation === 1 || selectedQuestLine.automaticActivation === true ? 1 : 0
      });
    } else {
      setQuestLineFormData(null);
    }
  }, [selectedQuestLine]);

  // Fetch all tasks for each quest to get titles
  const { data: allQuestTasks = {} } = useQuery({
    queryKey: ['allQuestTasks', selectedQuestLine?.id],
    queryFn: async () => {
      if (!quests || quests.length === 0) return {};
      const tasksMap = {};
      await Promise.all(
        quests.map(async (quest) => {
          try {
            const response = await api.get(`/quests/quests/${quest.id}/tasks`);
            tasksMap[quest.id] = response.data;
          } catch (error) {
            console.error(`Failed to fetch tasks for quest ${quest.id}:`, error);
            tasksMap[quest.id] = [];
          }
        })
      );
      return tasksMap;
    },
    enabled: !!selectedQuestLine && quests.length > 0,
    staleTime: 2 * 60 * 1000
  });

  // Fetch tasks for selected quest
  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['questTasks', selectedQuest?.id],
    queryFn: async () => {
      const response = await api.get(`/quests/quests/${selectedQuest.id}/tasks`);
      return response.data;
    },
    enabled: !!selectedQuest,
    staleTime: 2 * 60 * 1000
  });

  // Get quest title (use first task title if no quest title)
  const getQuestTitle = (quest, questTasks = []) => {
    if (quest.title) return quest.title;
    if (questTasks.length > 0 && questTasks[0].title) return questTasks[0].title;
    return 'Untitled Quest';
  };

  // Get first quest title for a questline
  const getFirstQuestTitle = (questLineId) => {
    const questsForLine = allQuests
      .filter(q => q.questLineId === questLineId)
      .sort((a, b) => (a.questOrder || 0) - (b.questOrder || 0));
    
    if (questsForLine.length === 0) return null;
    
    const firstQuest = questsForLine[0];
    const questTasks = allTasks.filter(t => t.questId === firstQuest.id);
    return getQuestTitle(firstQuest, questTasks);
  };

  // Helper to parse JSON fields
  const parseJSON = (field) => {
    try {
      if (typeof field === 'string') {
        return JSON.parse(field);
      }
      return field || [];
    } catch {
      return [];
    }
  };

  // Comprehensive search function
  const searchResults = useMemo(() => {
    if (!searchTerm) return null;
    
    const term = searchTerm.toLowerCase();
    const results = {
      questLines: [],
      quests: [],
      tasks: []
    };

    // Search quest lines by name, description, or GUID
    questLines.forEach(ql => {
      const matchesName = ql.displayName?.toLowerCase().includes(term) || 
                         ql.description?.toLowerCase().includes(term);
      const matchesGuid = ql.id?.toLowerCase().includes(term);
      
      if (matchesName || matchesGuid) {
        results.questLines.push(ql);
      }
    });

    // Search quests by name, description, GUID, or rewards
    allQuests.forEach(quest => {
      const rewards = parseJSON(quest.rewards);
      const itemsToGive = parseJSON(quest.itemsToGive);
      
      const matchesName = quest.title?.toLowerCase().includes(term) ||
                         quest.description?.toLowerCase().includes(term);
      const matchesGuid = quest.id?.toLowerCase().includes(term);
      const matchesRewards = rewards.some(r => {
        const asset = assetMap.get(r.targetId);
        return asset?.title?.toLowerCase().includes(term) || 
               r.targetId?.toLowerCase().includes(term);
      });
      const matchesItemsToGive = itemsToGive.some(item => {
        const asset = assetMap.get(item.targetId);
        return asset?.title?.toLowerCase().includes(term) || 
               item.targetId?.toLowerCase().includes(term);
      });
      
      if (matchesName || matchesGuid || matchesRewards || matchesItemsToGive) {
        results.quests.push(quest);
      }
    });

    // Search tasks by name, GUID, or requested items
    allTasks.forEach(task => {
      const requestedItems = parseJSON(task.requestedItems);
      const targetIdsList = task.targetIds ? task.targetIds.split(',').map(id => id.trim()).filter(Boolean) : [];
      
      const matchesName = task.title?.toLowerCase().includes(term) ||
                         task.description?.toLowerCase().includes(term);
      const matchesGuid = task.id?.toLowerCase().includes(term);
      const matchesRequestedItems = requestedItems.some(item => {
        const asset = assetMap.get(item.targetId);
        return asset?.title?.toLowerCase().includes(term) || 
               item.targetId?.toLowerCase().includes(term);
      });
      const matchesTargetIds = targetIdsList.some(targetId => {
        const asset = assetMap.get(targetId);
        return asset?.title?.toLowerCase().includes(term) || 
               targetId?.toLowerCase().includes(term);
      });
      
      if (matchesName || matchesGuid || matchesRequestedItems || matchesTargetIds) {
        results.tasks.push(task);
      }
    });

    return results;
  }, [searchTerm, questLines, allQuests, allTasks, assetMap]);

  // Filter quest lines by search term and quest line type (for list view when not searching)
  const filteredQuestLines = useMemo(() => {
    if (searchTerm && searchResults) {
      // Return search results instead
      return searchResults.questLines;
    }
    
    let filtered = questLines;
    
    // Filter by quest line type
    if (selectedQuestLineType) {
      filtered = filtered.filter(ql => ql.questLineType === selectedQuestLineType);
    }
    
    return filtered;
  }, [questLines, searchTerm, selectedQuestLineType, searchResults]);

  // Navigate to task's parent quest and quest line
  const navigateToTask = (task) => {
    if (task.questLine && task.quest) {
      setSelectedQuestLine(task.questLine);
      setSelectedQuest(task.quest);
      setSearchTerm(''); // Clear search after navigation
    }
  };

  // Helper to get asset name by ID
  const getAssetName = (assetId) => {
    const asset = assetMap.get(assetId);
    return asset ? asset.title : assetId.substring(0, 8) + '...';
  };

  // Helper to get quest line name by GUID
  const getQuestLineName = (guid) => {
    const questLine = questLines.find(ql => ql.id === guid);
    return questLine ? questLine.displayName : null;
  };

  // Helper to get quest name by GUID
  const getQuestName = (guid) => {
    const quest = allQuests.find(q => q.id === guid);
    if (!quest) return null;
    const questTasks = allTasks.filter(t => t.questId === quest.id);
    return getQuestTitle(quest, questTasks);
  };

  // Helper to get task name by GUID
  const getTaskName = (guid) => {
    const task = allTasks.find(t => t.id === guid);
    return task ? task.title : null;
  };

  // Helper to format GUID with name
  const formatGuidWithName = (guid, getNameFn) => {
    const name = getNameFn(guid);
    if (name) {
      return `${name} (${guid})`;
    }
    return guid;
  };

  // Helper to parse requirements
  const parseRequirements = (requirements) => {
    try {
      if (typeof requirements === 'string') {
        return JSON.parse(requirements);
      }
      return requirements || [];
    } catch {
      return [];
    }
  };

  // Calculate quest line progress
  const getQuestLineProgress = (questLine) => {
    const questsForLine = quests.filter(q => q.questLineId === questLine.id);
    return `${questsForLine.length} quests`;
  };

  // Handle copy GUID to clipboard
  const handleCopyGuid = async (guid, e) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(guid);
      setSnackbar({ open: true, message: 'GUID copied to clipboard!', severity: 'success' });
    } catch (err) {
      console.error('Failed to copy:', err);
      setSnackbar({ open: true, message: 'Failed to copy GUID', severity: 'error' });
    }
  };

  // Handle quest line save (inline)
  const handleQuestLineSaveInline = async () => {
    if (!selectedQuestLine || !questLineFormData) return;
    setSavingQuestLine(true);
    try {
      const saveData = {
        ...questLineFormData,
        requirements: JSON.stringify(questLineFormData.requirements)
      };
      await api.put(`/quests/quest-lines/${selectedQuestLine.id}`, saveData);
      setSnackbar({ open: true, message: 'Quest line updated successfully', severity: 'success' });
      queryClient.invalidateQueries(['questLines']);
      // Update selected quest line with new data
      const updatedQuestLine = { ...selectedQuestLine, ...questLineFormData };
      setSelectedQuestLine(updatedQuestLine);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'Failed to update quest line', severity: 'error' });
    } finally {
      setSavingQuestLine(false);
    }
  };

  // Handle quest line save (from dialog - for new quest lines)
  const handleQuestLineSave = async (formData) => {
    try {
      if (questLineEditDialog.questLine) {
        // Update existing quest line
        await api.put(`/quests/quest-lines/${questLineEditDialog.questLine.id}`, formData);
        setSnackbar({ open: true, message: 'Quest line updated successfully', severity: 'success' });
        setSelectedQuestLine(prev => ({ ...prev, ...formData }));
      } else {
        // Create new quest line
        await api.post('/quests/quest-lines', formData);
        setSnackbar({ open: true, message: 'Quest line created successfully', severity: 'success' });
      }
      setQuestLineEditDialog({ open: false, questLine: null });
      queryClient.invalidateQueries(['questLines']);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  // Handle quest save (inline)
  const handleQuestSaveInline = async () => {
    if (!selectedQuest || !questFormData) return;
    setSavingQuest(true);
    try {
      const saveData = {
        ...questFormData,
        itemsToGive: JSON.stringify(questFormData.itemsToGive),
        rewards: JSON.stringify(questFormData.rewards)
      };
      await api.put(`/quests/quests/${selectedQuest.id}`, saveData);
      setSnackbar({ open: true, message: 'Quest updated successfully', severity: 'success' });
      queryClient.invalidateQueries(['quests', selectedQuestLine?.id]);
      queryClient.invalidateQueries(['allQuestTasks', selectedQuestLine?.id]);
      // Update selected quest with new data
      const updatedQuest = { ...selectedQuest, ...questFormData };
      setSelectedQuest(updatedQuest);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'Failed to update quest', severity: 'error' });
    } finally {
      setSavingQuest(false);
    }
  };

  // Handle quest save (from dialog - for new quests)
  const handleQuestSave = async (formData) => {
    try {
      if (questEditDialog.quest) {
        // Update existing quest
        await api.put(`/quests/quests/${questEditDialog.quest.id}`, formData);
        setSnackbar({ open: true, message: 'Quest updated successfully', severity: 'success' });
      } else {
        // Create new quest
        await api.post('/quests/quests', {
          ...formData,
          questLineId: selectedQuestLine.id
        });
        setSnackbar({ open: true, message: 'Quest created successfully', severity: 'success' });
      }
      setQuestEditDialog({ open: false, quest: null });
      queryClient.invalidateQueries(['quests', selectedQuestLine?.id]);
      queryClient.invalidateQueries(['allQuestTasks', selectedQuestLine?.id]);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  // Handle task save
  const handleTaskSave = async (formData) => {
    try {
      if (taskEditDialog.task) {
        // Update existing task
        await api.put(`/quests/quest-tasks/${taskEditDialog.task.id}`, formData);
        setSnackbar({ open: true, message: 'Task updated successfully', severity: 'success' });
      } else {
        // Create new task
        await api.post('/quests/quest-tasks', {
          ...formData,
          questId: selectedQuest.id
        });
        setSnackbar({ open: true, message: 'Task created successfully', severity: 'success' });
      }
      setTaskEditDialog({ open: false, task: null });
      queryClient.invalidateQueries(['questTasks', selectedQuest?.id]);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  // Handle quest copy
  const handleQuestCopy = async (questToCopy) => {
    if (!questToCopy || !selectedQuestLine) return;
    
    try {
      // Fetch tasks for the quest to copy
      const tasksResponse = await api.get(`/quests/quests/${questToCopy.id}/tasks`);
      const tasksToCopy = tasksResponse.data;
      
      // Create new quest with copied data
      const newQuestData = {
        title: `${questToCopy.title || 'Untitled Quest'} (Copy)`,
        description: questToCopy.description || '',
        questOrder: quests.length, // Add at the end
        itemsToGive: questToCopy.itemsToGive || [],
        rewards: questToCopy.rewards || [],
        isActive: questToCopy.isActive || 1,
        conversationIds: questToCopy.conversationIds || {
          startConversationId: null,
          endConversationId: null
        }
      };
      
      // Create the quest
      const questResponse = await api.post('/quests/quests', {
        ...newQuestData,
        questLineId: selectedQuestLine.id
      });
      
      const newQuestId = questResponse.data.id;
      
      // Copy all tasks
      for (const task of tasksToCopy) {
        await api.post('/quests/quest-tasks', {
          questId: newQuestId,
          taskOrder: task.taskOrder || 0,
          title: task.title || '',
          dataPoint: task.dataPoint || '',
          amount: task.amount || 1,
          timeframe: task.timeframe || 'TaskStart',
          targetIds: task.targetIds || '',
          scene: task.scene || '',
          requestedItems: task.requestedItems || [],
          conversationIds: task.conversationIds || {
            startConversationId: null,
            endConversationId: null,
            failedConversationId: null
          },
          isActive: task.isActive || 1
        });
      }
      
      setSnackbar({ open: true, message: 'Quest copied successfully', severity: 'success' });
      queryClient.invalidateQueries(['quests', selectedQuestLine?.id]);
      queryClient.invalidateQueries(['allQuestTasks', selectedQuestLine?.id]);
      
      // Select the newly created quest
      const updatedQuestsResponse = await api.get(`/quests/quest-lines/${selectedQuestLine.id}/quests`);
      const newQuest = updatedQuestsResponse.data.find(q => q.id === newQuestId);
      if (newQuest) {
        setSelectedQuest(newQuest);
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'Failed to copy quest', severity: 'error' });
    }
  };

  // Handle quest delete
  const handleQuestDelete = async (questId) => {
    const targetQuestId = questId || selectedQuest?.id;
    if (!targetQuestId) return;
    try {
      await api.delete(`/quests/quests/${targetQuestId}`);
      setSnackbar({ open: true, message: 'Quest deleted successfully', severity: 'success' });
      // Clear selection if deleted quest was selected
      if (selectedQuest?.id === targetQuestId) {
        setSelectedQuest(null);
        setQuestFormData(null);
      }
      queryClient.invalidateQueries(['quests', selectedQuestLine?.id]);
      queryClient.invalidateQueries(['allQuestTasks', selectedQuestLine?.id]);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'Failed to delete quest', severity: 'error' });
    }
  };

  // Handle task delete
  const handleTaskDelete = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/quests/quest-tasks/${taskId}`);
      setSnackbar({ open: true, message: 'Task deleted successfully', severity: 'success' });
      queryClient.invalidateQueries(['questTasks', selectedQuest?.id]);
      queryClient.invalidateQueries(['allQuestTasks', selectedQuestLine?.id]);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'Failed to delete task', severity: 'error' });
    }
  };

  // Handle quest line copy
  const handleQuestLineCopy = async (questLineToCopy) => {
    if (!questLineToCopy) return;
    
    try {
      // Fetch quests for the questline to copy
      const questsResponse = await api.get(`/quests/quest-lines/${questLineToCopy.id}/quests`);
      const questsToCopy = questsResponse.data;
      
      // Create new questline with copied data
      const newQuestLineData = {
        displayName: `${questLineToCopy.displayName || 'Untitled Quest Line'} (Copy)`,
        description: questLineToCopy.description || '',
        questLineType: questLineToCopy.questLineType || 'story',
        requirements: questLineToCopy.requirements || [],
        isActive: questLineToCopy.isActive || 1,
        sortOrder: questLines.length, // Add at the end
        targetPassTier: questLineToCopy.targetPassTier || null,
        targetDay: questLineToCopy.targetDay || null,
        automaticActivation: questLineToCopy.automaticActivation || 0,
        conversationIds: questLineToCopy.conversationIds || {
          offerConversationId: null,
          startConversationId: null,
          endConversationId: null
        }
      };
      
      // Create the questline
      const questLineResponse = await api.post('/quests/quest-lines', newQuestLineData);
      const newQuestLineId = questLineResponse.data.id;
      
      // Copy all quests and their tasks
      for (const quest of questsToCopy) {
        // Fetch tasks for this quest
        const tasksResponse = await api.get(`/quests/quests/${quest.id}/tasks`);
        const tasksToCopy = tasksResponse.data;
        
        // Create new quest
        const newQuestData = {
          title: quest.title || '',
          description: quest.description || '',
          questOrder: quest.questOrder || 0,
          itemsToGive: quest.itemsToGive || [],
          rewards: quest.rewards || [],
          isActive: quest.isActive || 1,
          conversationIds: quest.conversationIds || {
            startConversationId: null,
            endConversationId: null
          }
        };
        
        const questResponse = await api.post('/quests/quests', {
          ...newQuestData,
          questLineId: newQuestLineId
        });
        const newQuestId = questResponse.data.id;
        
        // Copy all tasks for this quest
        for (const task of tasksToCopy) {
          await api.post('/quests/quest-tasks', {
            questId: newQuestId,
            taskOrder: task.taskOrder || 0,
            title: task.title || '',
          dataPoint: task.dataPoint || '',
          amount: task.amount || 1,
          timeframe: task.timeframe || 'TaskStart',
          targetIds: task.targetIds || '',
          scene: task.scene || '',
          requestedItems: task.requestedItems || [],
          conversationIds: task.conversationIds || {
            startConversationId: null,
            endConversationId: null,
            failedConversationId: null
          },
          isActive: task.isActive || 1
        });
        }
      }
      
      setSnackbar({ open: true, message: 'Quest line copied successfully with all quests and tasks', severity: 'success' });
      queryClient.invalidateQueries(['questLines']);
      queryClient.invalidateQueries(['quests']);
      queryClient.invalidateQueries(['allQuestTasks']);
      
      // Select the newly created questline
      const updatedQuestLinesResponse = await api.get('/quests/quest-lines');
      const newQuestLine = updatedQuestLinesResponse.data.find(ql => ql.id === newQuestLineId);
      if (newQuestLine) {
        setSelectedQuestLine(newQuestLine);
        setSelectedQuest(null);
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'Failed to copy quest line', severity: 'error' });
    }
  };

  // Handle quest line delete
  const handleQuestLineDelete = async (questLineId) => {
    if (!confirm('Are you sure you want to delete this quest line? This will delete all quests and tasks within it.')) return;
    try {
      await api.delete(`/quests/quest-lines/${questLineId}`);
      setSnackbar({ open: true, message: 'Quest line deleted successfully', severity: 'success' });
      // Clear selection if deleted quest line was selected
      if (selectedQuestLine?.id === questLineId) {
        setSelectedQuestLine(null);
        setSelectedQuest(null);
        setQuestLineFormData(null);
        setQuestFormData(null);
      }
      queryClient.invalidateQueries(['questLines']);
      queryClient.invalidateQueries(['quests', questLineId]);
      queryClient.invalidateQueries(['allQuestTasks', questLineId]);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'Failed to delete quest line', severity: 'error' });
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const response = await api.get('/quests/export', {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quest-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSnackbar({ 
        open: true, 
        message: 'Quest data exported successfully', 
        severity: 'success' 
      });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'Failed to export quest data', 
        severity: 'error' 
      });
    }
  };

  // Handle export of selected questlines only
  const handleExportSelected = async () => {
    if (selectedForExport.size === 0) {
      setSnackbar({ open: true, message: 'No quest lines selected', severity: 'warning' });
      return;
    }
    try {
      const ids = Array.from(selectedForExport).join(',');
      const response = await api.get(`/quests/export?ids=${ids}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quest-export-selected-${selectedForExport.size}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSnackbar({ 
        open: true, 
        message: `Exported ${selectedForExport.size} quest line(s) successfully`, 
        severity: 'success' 
      });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'Failed to export selected quest lines', 
        severity: 'error' 
      });
    }
  };

  // Stage selected questlines for production import
  const handleStageForProd = async () => {
    if (selectedForExport.size === 0) {
      setSnackbar({ open: true, message: 'No quest lines selected', severity: 'warning' });
      return;
    }
    
    setStagingInProgress(true);
    try {
      const response = await api.post('/quests/stage', {
        ids: Array.from(selectedForExport)
      });
      
      setSnackbar({ 
        open: true, 
        message: `Staged ${response.data.questlineCount} quest line(s) for production: ${response.data.questlineNames.join(', ')}`, 
        severity: 'success' 
      });
      setSelectedForExport(new Set()); // Clear selection after staging
      refetchStagedFiles(); // Refresh staged files list
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'Failed to stage quest lines', 
        severity: 'error' 
      });
    } finally {
      setStagingInProgress(false);
    }
  };

  // View preview/diff for a staged file
  const handleViewStagedPreview = async (filename) => {
    setSelectedStagedFile(filename);
    setStagedFilePreview(null);
    try {
      const response = await api.get(`/quests/staged/${filename}/preview`);
      setStagedFilePreview(response.data);
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'Failed to load preview', 
        severity: 'error' 
      });
    }
  };

  // Import a staged file
  const handleImportStagedFile = async (filename) => {
    setImportingStagedFile(true);
    try {
      const response = await api.post(`/quests/staged/${filename}/import`);
      
      setSnackbar({ 
        open: true, 
        message: `Imported ${response.data.stats.importedQuestLines} quest line(s), ${response.data.stats.importedQuests} quest(s), ${response.data.stats.importedTasks} task(s)`, 
        severity: 'success' 
      });
      
      // Refresh data
      queryClient.invalidateQueries(['questLines']);
      queryClient.invalidateQueries(['quests']);
      queryClient.invalidateQueries(['allQuestTasks']);
      refetchStagedFiles();
      
      // Close preview
      setSelectedStagedFile(null);
      setStagedFilePreview(null);
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'Failed to import staged file', 
        severity: 'error' 
      });
    } finally {
      setImportingStagedFile(false);
    }
  };

  // Delete a staged file
  const handleDeleteStagedFile = async (filename) => {
    try {
      await api.delete(`/quests/staged/${filename}`);
      setSnackbar({ open: true, message: 'Staged file deleted', severity: 'success' });
      refetchStagedFiles();
      if (selectedStagedFile === filename) {
        setSelectedStagedFile(null);
        setStagedFilePreview(null);
      }
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'Failed to delete staged file', 
        severity: 'error' 
      });
    }
  };

  // Toggle questline selection for export
  const toggleQuestLineSelection = (questLineId, event) => {
    event.stopPropagation();
    setSelectedForExport(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questLineId)) {
        newSet.delete(questLineId);
      } else {
        newSet.add(questLineId);
      }
      return newSet;
    });
  };

  // Select all visible questlines
  const selectAllQuestLines = () => {
    setSelectedForExport(new Set(filteredQuestLines.map(ql => ql.id)));
  };

  // Deselect all questlines
  const deselectAllQuestLines = () => {
    setSelectedForExport(new Set());
  };

  // Handle file selection for import
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        setSnackbar({ open: true, message: 'Please select a JSON file', severity: 'error' });
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!importFile) {
      setSnackbar({ open: true, message: 'Please select a JSON file', severity: 'error' });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('clearExisting', clearExisting);

      // Create a request config that removes Content-Type so axios can set it with boundary
      const config = {
        headers: {}
      };
      // Delete Content-Type to let axios set it automatically for FormData
      delete config.headers['Content-Type'];

      const response = await api.post('/quests/import', formData, config);

      setImportResult(response.data);
      
      // Refresh all quest data
      queryClient.invalidateQueries(['questLines']);
      queryClient.invalidateQueries(['quests']);
      queryClient.invalidateQueries(['allQuestTasks']);
      
      // Reset selection
      setSelectedQuestLine(null);
      setSelectedQuest(null);
      
      // Check if there are errors
      const hasErrors = response.data.errors && response.data.errors.length > 0;
      
      if (response.data.success && !hasErrors) {
        setSnackbar({ 
          open: true, 
          message: `Import successful! ${response.data.stats.importedQuestLines} quest lines, ${response.data.stats.importedQuests} quests, ${response.data.stats.importedTasks} tasks imported.`, 
          severity: 'success' 
        });
        
        // Close dialog after a short delay only if no errors
        setTimeout(() => {
          setImportDialogOpen(false);
          setImportFile(null);
          setClearExisting(false);
          setImportResult(null);
        }, 3000);
      } else if (hasErrors) {
        setSnackbar({ 
          open: true, 
          message: `Import completed with ${response.data.errors.length} error(s). Please review the errors below.`, 
          severity: 'warning' 
        });
        // Don't close dialog - keep it open so user can see errors
      }
    } catch (error) {
      setImportResult({
        success: false,
        error: error.response?.data?.error || error.message
      });
      setSnackbar({ open: true, message: error.response?.data?.error || error.message, severity: 'error' });
    } finally {
      setImporting(false);
    }
  };

  if (questLinesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Pending Imports Banner */}
      {stagedFiles.count > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setStagedFilesDialogOpen(true)}
              startIcon={<InboxIcon />}
            >
              Review Imports
            </Button>
          }
        >
          <strong>{stagedFiles.count} questline export(s)</strong> pending import from development
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Quest Manager
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="flex-end">
          {/* Selection-based actions */}
          {selectedForExport.size > 0 && (
            <>
              <Chip 
                label={`${selectedForExport.size} selected`} 
                onDelete={deselectAllQuestLines}
                color="primary"
                sx={{ alignSelf: 'center' }}
              />
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExportSelected}
              >
                Export Selected
              </Button>
              {isDevelopment && (
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleStageForProd}
                  disabled={stagingInProgress}
                >
                  {stagingInProgress ? 'Staging...' : 'Push to Prod'}
                </Button>
              )}
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export All
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import JSON
          </Button>
          {stagedFiles.count > 0 && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<InboxIcon />}
              onClick={() => setStagedFilesDialogOpen(true)}
            >
              Pending ({stagedFiles.count})
            </Button>
          )}
        </Stack>
      </Box>

      <Box 
        display="flex" 
        gap={2} 
        sx={{
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Left Panel - Quest Lines List or Tree */}
        <Card sx={{ 
          flex: { xs: 'none', md: 1 }, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'visible', 
          minWidth: 0,
          width: { xs: '100%', md: 'auto' },
          maxWidth: '100%'
        }}>
          <CardContent sx={{ flexShrink: 0, overflow: 'visible', maxWidth: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">Quest Lines</Typography>
              <Stack direction="row" spacing={0.5}>
                {selectedQuestLine && (
                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={() => setQuestLineEditDialog({ open: true, questLine: selectedQuestLine })}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => setQuestLineEditDialog({ open: true, questLine: null })}
                >
                  <AddIcon />
                </IconButton>
              </Stack>
            </Stack>
            
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Quest Line Type</InputLabel>
              <Select
                value={selectedQuestLineType}
                label="Quest Line Type"
                onChange={(e) => setSelectedQuestLineType(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="story">Story</MenuItem>
                <MenuItem value="paradise_pass">Paradise Pass</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="event">Event</MenuItem>
                <MenuItem value="side">Side</MenuItem>
              </Select>
            </FormControl>

            {/* Select/Deselect All buttons */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={selectAllQuestLines}
                startIcon={<CheckBoxIcon />}
                disabled={filteredQuestLines.length === 0}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={deselectAllQuestLines}
                startIcon={<CheckBoxOutlineBlankIcon />}
                disabled={selectedForExport.size === 0}
              >
                Deselect All
              </Button>
            </Stack>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="list">
                <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
                List
              </ToggleButton>
              <ToggleButton value="tree">
                <AccountTreeIcon fontSize="small" sx={{ mr: 0.5 }} />
                Tree
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              fullWidth
              size="small"
              placeholder="Search quest lines, quests, tasks by name, GUID, or rewards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{ mb: viewMode === 'list' ? 0 : 2 }}
            />
          </CardContent>
          <Divider />
          
          {viewMode === 'tree' && !searchTerm ? (
            <Box sx={{ 
              overflow: 'visible', 
              flexGrow: 1, 
              py: 1,
              minHeight: 0
            }}>
              <QuestLineTreeView 
                questLines={filteredQuestLines}
                onSelectQuestLine={(questLine) => {
                  setSelectedQuestLine(questLine);
                  setSelectedQuest(null);
                }}
                selectedQuestLineId={selectedQuestLine?.id}
              />
            </Box>
          ) : searchTerm && searchResults ? (
            <Box sx={{ 
              overflow: 'visible', 
              flexGrow: 1, 
              py: 1,
              minHeight: 0
            }}>
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Search Results
                </Typography>
              </Box>
              
              {/* Quest Lines Results */}
              {searchResults.questLines.length > 0 && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                    Quest Lines ({searchResults.questLines.length})
                  </Typography>
                  <List dense>
                    {searchResults.questLines.map((questLine) => {
                      const isActive = questLine.isActive === 1 || questLine.isActive === true;
                      const firstQuestTitle = getFirstQuestTitle(questLine.id);
                      return (
                        <ListItem 
                          key={questLine.id} 
                          disablePadding
                          secondaryAction={
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Copy Quest Line">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuestLineCopy(questLine);
                                  }}
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Quest Line ID">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={(e) => handleCopyGuid(questLine.id, e)}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          }
                        >
                          <ListItemButton
                            selected={selectedQuestLine?.id === questLine.id}
                            onClick={() => {
                              setSelectedQuestLine(questLine);
                              setSelectedQuest(null);
                            }}
                          >
                            <ListItemText
                              primary={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: isActive ? '#10b981' : '#ef4444',
                                      flexShrink: 0
                                    }}
                                  />
                                  <Typography variant="body2">{questLine.displayName}</Typography>
                                </Stack>
                              }
                              secondary={
                                <Stack spacing={0.5} mt={0.5}>
                                  {firstQuestTitle && (
                                    <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                      {firstQuestTitle}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                    {formatGuidWithName(questLine.id, getQuestLineName)}
                                  </Typography>
                                </Stack>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {/* Quests Results */}
              {searchResults.quests.length > 0 && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                    Quests ({searchResults.quests.length})
                  </Typography>
                  <List dense>
                    {searchResults.quests.map((quest) => {
                      const questTasks = allTasks.filter(t => t.questId === quest.id);
                      const questTitle = getQuestTitle(quest, questTasks);
                      return (
                        <ListItem 
                          key={quest.id} 
                          disablePadding
                          secondaryAction={
                            <Tooltip title="Copy Quest ID">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={(e) => handleCopyGuid(quest.id, e)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemButton
                            selected={selectedQuest?.id === quest.id}
                            onClick={() => {
                              setSelectedQuestLine(quest.questLine);
                              setSelectedQuest(quest);
                            }}
                          >
                            <ListItemText
                              primary={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="body2">{questTitle}</Typography>
                                  <Chip label={quest.questLine?.displayName || 'Unknown'} size="small" sx={{ height: '18px', fontSize: '0.65rem' }} />
                                </Stack>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                  {formatGuidWithName(quest.id, getQuestName)}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {/* Tasks Results */}
              {searchResults.tasks.length > 0 && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                    Tasks ({searchResults.tasks.length})
                  </Typography>
                  <List dense>
                    {searchResults.tasks.map((task) => (
                      <ListItem 
                        key={task.id} 
                        disablePadding
                          secondaryAction={
                            <Tooltip title="Copy Task ID">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={(e) => handleCopyGuid(task.id, e)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          }
                      >
                        <ListItemButton
                          onClick={() => navigateToTask(task)}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                <Typography variant="body2">{task.title}</Typography>
                                <Chip label={task.quest?.title || 'Unknown Quest'} size="small" sx={{ height: '18px', fontSize: '0.65rem' }} />
                                <Chip label={task.questLine?.displayName || 'Unknown Quest Line'} size="small" sx={{ height: '18px', fontSize: '0.65rem' }} />
                              </Stack>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                {formatGuidWithName(task.id, getTaskName)}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {searchResults.questLines.length === 0 && searchResults.quests.length === 0 && searchResults.tasks.length === 0 && (
                <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No results found matching &quot;{searchTerm}&quot;.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <List sx={{ 
              overflow: 'visible', 
              flexGrow: 1
            }}>
              {filteredQuestLines.length === 0 ? (
                <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No quest lines found matching the selected filter.
                  </Typography>
                </Box>
              ) : (
                filteredQuestLines.map((questLine) => {
                const requirements = parseRequirements(questLine.requirements);
                const isActive = questLine.isActive === 1 || questLine.isActive === true;
                const firstQuestTitle = getFirstQuestTitle(questLine.id);
                const isSelectedForExport = selectedForExport.has(questLine.id);
                
                return (
                  <ListItem 
                    key={questLine.id} 
                    disablePadding
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Copy Quest Line (duplicate with all quests and tasks)">
                          <IconButton
                            edge="end"
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuestLineCopy(questLine);
                            }}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy Quest Line ID">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => handleCopyGuid(questLine.id, e)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuestLineDelete(questLine.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <Checkbox
                      size="small"
                      checked={isSelectedForExport}
                      onClick={(e) => toggleQuestLineSelection(questLine.id, e)}
                      sx={{ ml: 1 }}
                    />
                    <ListItemButton
                      selected={selectedQuestLine?.id === questLine.id}
                      onClick={() => {
                        setSelectedQuestLine(questLine);
                        setSelectedQuest(null); // Will be auto-selected by onSuccess
                      }}
                      sx={{ pl: 0 }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: isActive ? '#10b981' : '#ef4444',
                                flexShrink: 0
                              }}
                            />
                            <Typography variant="body2" fontWeight="medium">
                              {questLine.displayName}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Stack spacing={0.5} mt={0.5}>
                            {firstQuestTitle && (
                              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                {firstQuestTitle}
                              </Typography>
                            )}
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              <Chip
                                label={questLine.questLineType || 'story'}
                                size="small"
                                sx={{ height: '20px', fontSize: '0.7rem' }}
                              />
                              {!isActive && (
                                <Chip
                                  label="Inactive"
                                  size="small"
                                  color="error"
                                  sx={{ height: '20px', fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            {requirements.length > 0 && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  Requires: {requirements.map(req => {
                                    if (req.dataPoint === 'QuestLineComplete') {
                                      const reqQuestLine = questLines.find(ql => ql.id === req.targetId);
                                      if (reqQuestLine) {
                                        return `${reqQuestLine.displayName} (${req.targetId})`;
                                      }
                                      return req.targetId;
                                    }
                                    if (req.dataPoint === 'TycoonBuilderLevelReached') {
                                      return `Tycoon Power: ${req.amount || req.targetId || '?'}`;
                                    }
                                    return req.dataPoint;
                                  }).join(', ')}
                                </Typography>
                              </Box>
                            )}
                            {questLine.conversationIds && (questLine.conversationIds.offerConversationId || questLine.conversationIds.startConversationId || questLine.conversationIds.endConversationId) && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  Conversations: {[
                                    questLine.conversationIds.offerConversationId && 'Offer',
                                    questLine.conversationIds.startConversationId && 'Start',
                                    questLine.conversationIds.endConversationId && 'End'
                                  ].filter(Boolean).join(', ')}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
                })
              )}
            </List>
          )}
        </Card>

        {/* Middle Panel - Quest Line Settings / Quests List */}
        <Card sx={{ 
          flex: { xs: 'none', md: 1 }, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden', 
          minWidth: 0,
          width: { xs: '100%', md: 'auto' },
          maxWidth: '100%',
          height: 'calc(100vh - 200px)'
        }}>
          {selectedQuestLine ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              height: '100%', 
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Scrollable Container */}
              <Box sx={{ 
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
                minHeight: 0,
                height: '100%'
              }}>
                {/* Quest Line Settings - Sticky */}
                <CardContent sx={{ 
                  flexShrink: 0, 
                  overflow: 'visible', 
                  maxWidth: '100%',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 10,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="bold">Quest Line Settings</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleQuestLineSaveInline}
                      disabled={savingQuestLine || !questLineFormData}
                    >
                      {savingQuestLine ? 'Saving...' : 'Save'}
                    </Button>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleQuestLineDelete(selectedQuestLine.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>
                {questLineFormData && (
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Display Name"
                      value={questLineFormData.displayName}
                      onChange={(e) => setQuestLineFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={questLineFormData.description || ''}
                      onChange={(e) => setQuestLineFormData(prev => ({ ...prev, description: e.target.value }))}
                      multiline
                      rows={2}
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={questLineFormData.questLineType}
                        label="Type"
                        onChange={(e) => setQuestLineFormData(prev => ({ ...prev, questLineType: e.target.value }))}
                      >
                        <MenuItem value="story">Story</MenuItem>
                        <MenuItem value="paradise_pass">Paradise Pass</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="event">Event</MenuItem>
                        <MenuItem value="side">Side</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={questLineFormData.isActive === 1}
                          onChange={(e) => setQuestLineFormData(prev => ({ ...prev, isActive: e.target.checked ? 1 : 0 }))}
                        />
                      }
                      label="Active"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={questLineFormData.automaticActivation === 1}
                          onChange={(e) => setQuestLineFormData(prev => ({ ...prev, automaticActivation: e.target.checked ? 1 : 0 }))}
                        />
                      }
                      label="Automatic Activation"
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Sort Order"
                      type="number"
                      value={questLineFormData.sortOrder}
                      onChange={(e) => setQuestLineFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                    />
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="text.secondary">Requirements</Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const newReq = { dataPoint: 'QuestLineComplete', amount: 1, targetId: '' };
                            setQuestLineFormData(prev => ({
                              ...prev,
                              requirements: [...(prev.requirements || []), newReq]
                            }));
                          }}
                        >
                          Add
                        </Button>
                      </Stack>
                      <Stack spacing={1}>
                        {(questLineFormData.requirements || []).map((req, idx) => {
                          const reqQuestLine = req.dataPoint === 'QuestLineComplete' 
                            ? questLines.find(ql => ql.id === req.targetId)
                            : null;
                          
                          return (
                            <Stack key={idx} direction="row" spacing={1} alignItems="center">
                              <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel>Type</InputLabel>
                                <Select
                                  value={req.dataPoint || 'QuestLineComplete'}
                                  label="Type"
                                  onChange={(e) => {
                                    const newDataPoint = e.target.value;
                                    setQuestLineFormData(prev => ({
                                      ...prev,
                                      requirements: prev.requirements.map((r, i) => 
                                        i === idx ? { ...r, dataPoint: newDataPoint, targetId: newDataPoint === 'QuestLineComplete' ? r.targetId : '' } : r
                                      )
                                    }));
                                  }}
                                >
                                  <MenuItem value="QuestLineComplete">Quest Line</MenuItem>
                                  <MenuItem value="TycoonBuilderLevelReached">Tycoon Level</MenuItem>
                                </Select>
                              </FormControl>

                              {req.dataPoint === 'QuestLineComplete' ? (
                                <Autocomplete
                                  size="small"
                                  options={questLines.filter(ql => ql.id !== selectedQuestLine?.id)}
                                  getOptionLabel={(option) => option.displayName || ''}
                                  value={reqQuestLine || null}
                                  onChange={(e, newValue) => {
                                    setQuestLineFormData(prev => ({
                                      ...prev,
                                      requirements: prev.requirements.map((r, i) => 
                                        i === idx ? { ...r, targetId: newValue?.id || '' } : r
                                      )
                                    }));
                                  }}
                                  sx={{ flexGrow: 1 }}
                                  renderInput={(params) => <TextField {...params} label="Quest Line" />}
                                />
                              ) : (
                                <TextField
                                  size="small"
                                  label="Target ID"
                                  value={req.targetId || ''}
                                  onChange={(e) => {
                                    setQuestLineFormData(prev => ({
                                      ...prev,
                                      requirements: prev.requirements.map((r, i) => 
                                        i === idx ? { ...r, targetId: e.target.value } : r
                                      )
                                    }));
                                  }}
                                  sx={{ flexGrow: 1 }}
                                />
                              )}

                              <TextField
                                size="small"
                                label="Amount"
                                type="number"
                                value={req.amount || 1}
                                onChange={(e) => {
                                  setQuestLineFormData(prev => ({
                                    ...prev,
                                    requirements: prev.requirements.map((r, i) => 
                                      i === idx ? { ...r, amount: parseInt(e.target.value) || 1 } : r
                                    )
                                  }));
                                }}
                                sx={{ width: 100 }}
                              />

                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setQuestLineFormData(prev => ({
                                    ...prev,
                                    requirements: prev.requirements.filter((_, i) => i !== idx)
                                  }));
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          );
                        })}
                        {(questLineFormData.requirements || []).length === 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
                            No requirements - this quest line will be available immediately
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    
                    {/* Conversation IDs */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" mb={1}>Conversation IDs</Typography>
                      <Stack spacing={1}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Offer Conversation ID"
                          value={questLineFormData.conversationIds?.offerConversationId || ''}
                          onChange={(e) => setQuestLineFormData(prev => ({
                            ...prev,
                            conversationIds: {
                              ...(prev.conversationIds || {}),
                              offerConversationId: e.target.value || null
                            }
                          }))}
                          placeholder="GUID for conversation shown when quest line is offered"
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Start Conversation ID"
                          value={questLineFormData.conversationIds?.startConversationId || ''}
                          onChange={(e) => setQuestLineFormData(prev => ({
                            ...prev,
                            conversationIds: {
                              ...(prev.conversationIds || {}),
                              startConversationId: e.target.value || null
                            }
                          }))}
                          placeholder="GUID for conversation shown when quest line starts"
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="End Conversation ID"
                          value={questLineFormData.conversationIds?.endConversationId || ''}
                          onChange={(e) => setQuestLineFormData(prev => ({
                            ...prev,
                            conversationIds: {
                              ...(prev.conversationIds || {}),
                              endConversationId: e.target.value || null
                            }
                          }))}
                          placeholder="GUID for conversation shown when quest line ends"
                        />
                      </Stack>
                    </Box>
                  </Stack>
                )}
                </CardContent>
                <Divider />
                {/* Quests List Header - Sticky */}
                <CardContent sx={{ 
                  flexShrink: 0, 
                  overflow: 'visible', 
                  maxWidth: '100%',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 9,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">Quests</Typography>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => setQuestEditDialog({ open: true, quest: null })}
                    >
                      <AddIcon />
                    </IconButton>
                  </Stack>
                </CardContent>
                <Divider />
                {/* Quests List */}
                <Box>
                {questsLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <List>
                    {quests.map((quest, index) => {
                      const rewards = parseJSON(quest.rewards);
                      const itemsToGive = parseJSON(quest.itemsToGive);
                      const questTasks = allQuestTasks[quest.id] || [];
                      const questTitle = getQuestTitle(quest, questTasks);
                      
                      return (
                        <ListItem 
                          key={quest.id} 
                          disablePadding
                          secondaryAction={
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Copy Quest (duplicate with all tasks)">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuestCopy(quest);
                                  }}
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Quest ID">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={(e) => handleCopyGuid(quest.id, e)}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                edge="end"
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm('Are you sure you want to delete this quest?')) {
                                    handleQuestDelete(quest.id);
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          }
                        >
                          <ListItemButton
                            selected={selectedQuest?.id === quest.id}
                            onClick={() => setSelectedQuest(quest)}
                          >
                            <ListItemText
                              primary={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Chip
                                    label={`#${index + 1}`}
                                    size="small"
                                    sx={{ height: '20px', fontSize: '0.7rem', minWidth: '40px' }}
                                  />
                                  <Typography variant="body2" fontWeight="medium">
                                    {questTitle}
                                  </Typography>
                                </Stack>
                              }
                              secondary={
                                <Stack spacing={0.5} mt={0.5}>
                                  {rewards.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        Rewards:
                                      </Typography>
                                      <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.25}>
                                        {rewards.map((reward, idx) => {
                                          const asset = assetMap.get(reward.targetId);
                                          const assetType = getAssetTypeLabel(asset);
                                          return (
                                            <Chip
                                              key={idx}
                                              label={`${reward.amount}x ${getAssetName(reward.targetId)} (${assetType})`}
                                              size="small"
                                              color="success"
                                              sx={{ height: '18px', fontSize: '0.65rem' }}
                                            />
                                          );
                                        })}
                                      </Stack>
                                    </Box>
                                  )}
                                  {itemsToGive.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        Starter items:
                                      </Typography>
                                      <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.25}>
                                        {itemsToGive.map((item, idx) => {
                                          const asset = assetMap.get(item.targetId);
                                          const assetType = getAssetTypeLabel(asset);
                                          return (
                                            <Chip
                                              key={idx}
                                              label={`${item.amount}x ${getAssetName(item.targetId)} (${assetType})`}
                                              size="small"
                                              color="info"
                                              sx={{ height: '18px', fontSize: '0.65rem' }}
                                            />
                                          );
                                        })}
                                      </Stack>
                                    </Box>
                                  )}
                                  {quest.conversationIds && (quest.conversationIds.startConversationId || quest.conversationIds.endConversationId) && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        Conversations: {[
                                          quest.conversationIds.startConversationId && 'Start',
                                          quest.conversationIds.endConversationId && 'End'
                                        ].filter(Boolean).join(', ')}
                                      </Typography>
                                    </Box>
                                  )}
                                </Stack>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
                </Box>
              </Box>
            </Box>
          ) : (
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Select a quest line to view settings and quests
              </Typography>
            </CardContent>
          )}
        </Card>

        {/* Right Panel - Quest Settings / Tasks List */}
        <Card sx={{ 
          flex: { xs: 'none', md: 1 }, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden', 
          minWidth: 0,
          width: { xs: '100%', md: 'auto' },
          maxWidth: '100%',
          height: 'calc(100vh - 200px)'
        }}>
          {selectedQuest ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              height: '100%', 
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Scrollable Container */}
              <Box sx={{ 
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
                minHeight: 0,
                height: '100%'
              }}>
                {/* Quest Settings - Sticky */}
                <CardContent sx={{ 
                  flexShrink: 0, 
                  overflow: 'visible', 
                  maxWidth: '100%',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 10,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="bold">Quest Settings</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleQuestSaveInline}
                      disabled={savingQuest || !questFormData}
                    >
                      {savingQuest ? 'Saving...' : 'Save'}
                    </Button>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={handleQuestDelete}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>
                
                {/* Quest Info */}
                {questFormData && (
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Title"
                      value={questFormData.title}
                      onChange={(e) => setQuestFormData(prev => ({ ...prev, title: e.target.value }))}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={questFormData.description || ''}
                      onChange={(e) => setQuestFormData(prev => ({ ...prev, description: e.target.value }))}
                      multiline
                      rows={2}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Order"
                      type="number"
                      value={questFormData.questOrder}
                      onChange={(e) => setQuestFormData(prev => ({ ...prev, questOrder: parseInt(e.target.value) || 0 }))}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={questFormData.isActive === 1}
                          onChange={(e) => setQuestFormData(prev => ({ ...prev, isActive: e.target.checked ? 1 : 0 }))}
                        />
                      }
                      label="Active"
                    />
                    
                    {/* Items to Give - Editable */}
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="text.secondary">Items Given at Start</Typography>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            const currentItems = questFormData.itemsToGive || [];
                            setQuestFormData(prev => ({
                              ...prev,
                              itemsToGive: [...currentItems, { targetId: '', amount: 1 }]
                            }));
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Stack spacing={1}>
                        {(questFormData.itemsToGive || []).map((item, idx) => {
                          const asset = assetMap.get(item.targetId);
                          const assetOptions = Array.from(assetMap.values()).map(a => ({
                            id: a.id,
                            label: `${a.title} (${getAssetTypeLabel(a)})`
                          }));
                          
                          return (
                            <Stack key={idx} direction="row" spacing={1} alignItems="center">
                              <Autocomplete
                                size="small"
                                options={assetOptions}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                filterOptions={(options, { inputValue }) => {
                                  if (!inputValue || !inputValue.trim()) return options;
                                  const searchTerm = inputValue.trim().toLowerCase();
                                  return options.filter(option => {
                                    const asset = assetMap.get(option.id);
                                    const title = (asset?.title || '').toLowerCase();
                                    const id = (option.id || '').toLowerCase();
                                    const label = (option.label || '').toLowerCase();
                                    return title.includes(searchTerm) || id.includes(searchTerm) || label.includes(searchTerm);
                                  });
                                }}
                                value={assetOptions.find(opt => opt.id === item.targetId) || null}
                                onChange={(e, newValue) => {
                                  const updatedItems = [...(questFormData.itemsToGive || [])];
                                  updatedItems[idx] = {
                                    ...updatedItems[idx],
                                    targetId: newValue?.id || ''
                                  };
                                  setQuestFormData(prev => ({ ...prev, itemsToGive: updatedItems }));
                                }}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props}>
                                    <Box>
                                      <Typography variant="body2">
                                        {option.label}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        {truncateGuid(option.id)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Select item" size="small" />
                                )}
                                sx={{ flexGrow: 1 }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                value={item.amount || 1}
                                onChange={(e) => {
                                  const updatedItems = [...(questFormData.itemsToGive || [])];
                                  updatedItems[idx] = {
                                    ...updatedItems[idx],
                                    amount: parseInt(e.target.value) || 1
                                  };
                                  setQuestFormData(prev => ({ ...prev, itemsToGive: updatedItems }));
                                }}
                                inputProps={{ min: 1, style: { width: '60px', textAlign: 'center' } }}
                              />
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  const updatedItems = (questFormData.itemsToGive || []).filter((_, i) => i !== idx);
                                  setQuestFormData(prev => ({ ...prev, itemsToGive: updatedItems }));
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          );
                        })}
                        {(!questFormData.itemsToGive || questFormData.itemsToGive.length === 0) && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No items. Click + to add.
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Rewards - Editable */}
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="text.secondary">Rewards</Typography>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            const currentRewards = questFormData.rewards || [];
                            setQuestFormData(prev => ({
                              ...prev,
                              rewards: [...currentRewards, { targetId: '', amount: 1 }]
                            }));
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Stack spacing={1}>
                        {(questFormData.rewards || []).map((reward, idx) => {
                          const asset = assetMap.get(reward.targetId);
                          const assetOptions = Array.from(assetMap.values()).map(a => ({
                            id: a.id,
                            label: `${a.title} (${getAssetTypeLabel(a)})`
                          }));
                          
                          return (
                            <Stack key={idx} direction="row" spacing={1} alignItems="center">
                              <Autocomplete
                                size="small"
                                options={assetOptions}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                filterOptions={(options, { inputValue }) => {
                                  if (!inputValue || !inputValue.trim()) return options;
                                  const searchTerm = inputValue.trim().toLowerCase();
                                  return options.filter(option => {
                                    const asset = assetMap.get(option.id);
                                    const title = (asset?.title || '').toLowerCase();
                                    const id = (option.id || '').toLowerCase();
                                    const label = (option.label || '').toLowerCase();
                                    return title.includes(searchTerm) || id.includes(searchTerm) || label.includes(searchTerm);
                                  });
                                }}
                                value={assetOptions.find(opt => opt.id === reward.targetId) || null}
                                onChange={(e, newValue) => {
                                  const updatedRewards = [...(questFormData.rewards || [])];
                                  updatedRewards[idx] = {
                                    ...updatedRewards[idx],
                                    targetId: newValue?.id || ''
                                  };
                                  setQuestFormData(prev => ({ ...prev, rewards: updatedRewards }));
                                }}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props}>
                                    <Box>
                                      <Typography variant="body2">
                                        {option.label}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        {truncateGuid(option.id)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Select item" size="small" />
                                )}
                                sx={{ flexGrow: 1 }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                value={reward.amount || 1}
                                onChange={(e) => {
                                  const updatedRewards = [...(questFormData.rewards || [])];
                                  updatedRewards[idx] = {
                                    ...updatedRewards[idx],
                                    amount: parseInt(e.target.value) || 1
                                  };
                                  setQuestFormData(prev => ({ ...prev, rewards: updatedRewards }));
                                }}
                                inputProps={{ min: 1, style: { width: '60px', textAlign: 'center' } }}
                              />
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  const updatedRewards = (questFormData.rewards || []).filter((_, i) => i !== idx);
                                  setQuestFormData(prev => ({ ...prev, rewards: updatedRewards }));
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          );
                        })}
                        {(!questFormData.rewards || questFormData.rewards.length === 0) && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No rewards. Click + to add.
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    
                    {/* Conversation IDs */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" mb={1}>Conversation IDs</Typography>
                      <Stack spacing={1}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Start Conversation ID"
                          value={questFormData.conversationIds?.startConversationId || ''}
                          onChange={(e) => setQuestFormData(prev => ({
                            ...prev,
                            conversationIds: {
                              ...(prev.conversationIds || {}),
                              startConversationId: e.target.value || null
                            }
                          }))}
                          placeholder="GUID for conversation shown when quest starts"
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="End Conversation ID"
                          value={questFormData.conversationIds?.endConversationId || ''}
                          onChange={(e) => setQuestFormData(prev => ({
                            ...prev,
                            conversationIds: {
                              ...(prev.conversationIds || {}),
                              endConversationId: e.target.value || null
                            }
                          }))}
                          placeholder="GUID for conversation shown when quest ends"
                        />
                      </Stack>
                    </Box>
                  </Stack>
                )}
                </CardContent>
                <Divider />
                
                {/* Tasks List Header - Sticky */}
                <CardContent sx={{ 
                  flexShrink: 0, 
                  overflow: 'visible', 
                  maxWidth: '100%',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 9,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">Tasks</Typography>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => setTaskEditDialog({ open: true, task: null })}
                    >
                      <AddIcon />
                    </IconButton>
                  </Stack>
                </CardContent>
                <Divider />
                
                {/* Tasks List */}
                <Box>
                {tasksLoading ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : tasks.length > 0 ? (
                  <CardContent>
                    <Stack spacing={2}>
                      {tasks.map((task, index) => {
                        const requestedItems = parseJSON(task.requestedItems);
                        const targetIdsList = task.targetIds ? task.targetIds.split(',').map(id => id.trim()).filter(Boolean) : [];
                        
                        return (
                          <Card key={task.id} variant="outlined" sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                              <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="start">
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="bold"
                                    onClick={() => {
                                      // Task is already in context of selectedQuest and selectedQuestLine
                                      if (selectedQuest && selectedQuestLine) {
                                        navigateToTask({ ...task, quest: selectedQuest, questLine: selectedQuestLine });
                                      }
                                    }}
                                    sx={{ cursor: 'pointer', flexGrow: 1 }}
                                  >
                                    {index + 1}. {task.title}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Copy Task ID">
                                      <IconButton 
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyGuid(task.id, e);
                                        }}
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <IconButton 
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setTaskEditDialog({ open: true, task });
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTaskDelete(task.id);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                                
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                  <Chip label={task.dataPoint} size="small" sx={{ height: '20px', fontSize: '0.7rem' }} />
                                  <Chip label={task.timeframe} size="small" color="primary" sx={{ height: '20px', fontSize: '0.7rem' }} />
                                  <Chip label={`Amount: ${task.amount}`} size="small" sx={{ height: '20px', fontSize: '0.7rem' }} />
                                  <Chip label={`Order: ${task.taskOrder || 0}`} size="small" sx={{ height: '20px', fontSize: '0.7rem' }} />
                                </Box>
                                
                                {targetIdsList.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                      Target IDs:
                                    </Typography>
                                    <Stack spacing={0.5}>
                                      {targetIdsList.map((targetId, idx) => {
                                        const asset = assetMap.get(targetId);
                                        const assetType = asset ? getAssetTypeLabel(asset) : 'OTHER';
                                        
                                        return (
                                          <Box key={idx}>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                              {asset ? `${asset.title} (${assetType})` : 'OTHER'}:{' '}
                                              <span style={{ fontFamily: 'monospace', opacity: 0.7 }}>{targetId}</span>
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  </Box>
                                )}
                                
                                {requestedItems.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                      Required Items:
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                      {requestedItems.map((item, idx) => {
                                        const asset = assetMap.get(item.targetId);
                                        const assetType = getAssetTypeLabel(asset);
                                        return (
                                          <Chip
                                            key={idx}
                                            label={`${item.amount}x ${getAssetName(item.targetId)} (${assetType})`}
                                            size="small"
                                            color="warning"
                                            sx={{ height: '22px', fontSize: '0.7rem' }}
                                          />
                                        );
                                      })}
                                    </Stack>
                                  </Box>
                                )}
                                
                                {task.conversationIds && (task.conversationIds.startConversationId || task.conversationIds.endConversationId || task.conversationIds.failedConversationId) && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                      Conversation IDs:
                                    </Typography>
                                    <Stack spacing={0.5}>
                                      {task.conversationIds.startConversationId && (
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                          Start: <span style={{ fontFamily: 'monospace', opacity: 0.7 }}>{task.conversationIds.startConversationId}</span>
                                        </Typography>
                                      )}
                                      {task.conversationIds.endConversationId && (
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                          End: <span style={{ fontFamily: 'monospace', opacity: 0.7 }}>{task.conversationIds.endConversationId}</span>
                                        </Typography>
                                      )}
                                      {task.conversationIds.failedConversationId && (
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                          Failed: <span style={{ fontFamily: 'monospace', opacity: 0.7 }}>{task.conversationIds.failedConversationId}</span>
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Box>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  </CardContent>
                ) : (
                  <CardContent>
                    <Alert severity="info">No tasks for this quest</Alert>
                  </CardContent>
                )}
                </Box>
              </Box>
            </Box>
          ) : (
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Select a quest to view settings and tasks
              </Typography>
            </CardContent>
          )}
        </Card>
      </Box>

      {/* Edit Dialogs */}
      <QuestLineEditDialog
        open={questLineEditDialog.open}
        onClose={() => setQuestLineEditDialog({ open: false, questLine: null })}
        questLine={questLineEditDialog.questLine}
        allQuestLines={questLines}
        onSave={handleQuestLineSave}
      />

      <QuestEditDialog
        open={questEditDialog.open}
        onClose={() => setQuestEditDialog({ open: false, quest: null })}
        quest={questEditDialog.quest}
        assets={assets}
        onSave={handleQuestSave}
      />

      <TaskEditDialog
        open={taskEditDialog.open}
        onClose={() => setTaskEditDialog({ open: false, task: null })}
        task={taskEditDialog.task}
        assets={assets}
        onSave={handleTaskSave}
      />

      {/* Import Dialog */}
      <Dialog 
        open={importDialogOpen} 
        onClose={() => !importing && setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Quests from JSON</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <input
                accept=".json,application/json"
                style={{ display: 'none' }}
                id="import-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="import-file-input">
                <Button variant="outlined" component="span" fullWidth>
                  {importFile ? importFile.name : 'Select JSON File'}
                </Button>
              </label>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={clearExisting}
                  onChange={(e) => setClearExisting(e.target.checked)}
                  disabled={importing}
                />
              }
              label="Clear existing quests before import (WARNING: This will delete all current quests)"
            />

            {importing && (
              <Box>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Importing quests...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {importResult && (
              <Box>
                {importResult.success ? (
                  <Alert severity="success">
                    <Typography variant="body2" fontWeight="bold">Import Successful!</Typography>
                    <Typography variant="body2">
                      Quest Lines: {importResult.stats.importedQuestLines}
                      {importResult.stats.skippedQuestLines > 0 && ` (${importResult.stats.skippedQuestLines} skipped)`}
                    </Typography>
                    <Typography variant="body2">
                      Quests: {importResult.stats.importedQuests}
                    </Typography>
                    <Typography variant="body2">
                      Tasks: {importResult.stats.importedTasks}
                    </Typography>
                    {importResult.stats.errors > 0 && (
                      <Typography variant="body2" color="warning.main">
                        Errors: {importResult.stats.errors}
                      </Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <Typography variant="body2" fontWeight="bold">Import Failed</Typography>
                    <Typography variant="body2">{importResult.error}</Typography>
                  </Alert>
                )}
                
                {importResult.errors && importResult.errors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold" mb={1}>
                      Import Errors:
                    </Typography>
                    <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                      {importResult.errors.map((error, idx) => (
                        <Typography key={idx} variant="caption" display="block" color="error">
                          • {error}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setImportDialogOpen(false);
            setImportFile(null);
            setClearExisting(false);
            setImportResult(null);
          }} disabled={importing}>
            Close
          </Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            disabled={!importFile || importing}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staged Files Dialog */}
      <Dialog 
        open={stagedFilesDialogOpen} 
        onClose={() => {
          setStagedFilesDialogOpen(false);
          setSelectedStagedFile(null);
          setStagedFilePreview(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Pending Questline Imports</Typography>
            <IconButton onClick={() => refetchStagedFiles()} size="small">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {stagedFiles.count === 0 ? (
            <Alert severity="info">No pending imports from development</Alert>
          ) : (
            <Stack spacing={2}>
              {/* File List */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Staged Files ({stagedFiles.count})</Typography>
                <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                  {stagedFiles.files.map((file) => (
                    <ListItem 
                      key={file.filename}
                      secondaryAction={
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Preview Changes">
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewStagedPreview(file.filename)}
                              color={selectedStagedFile === file.filename ? 'primary' : 'default'}
                            >
                              <PreviewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteStagedFile(file.filename)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      }
                    >
                      <ListItemButton 
                        selected={selectedStagedFile === file.filename}
                        onClick={() => handleViewStagedPreview(file.filename)}
                      >
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <InboxIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {file.questlineCount || '?'} questline(s)
                              </Typography>
                              <Chip 
                                label={new Date(file.stagedAt || file.createdAt).toLocaleString()} 
                                size="small" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Stack>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {file.questlineNames?.join(', ') || file.filename}
                              {file.stagedBy && ` • by ${file.stagedBy}`}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Preview Panel */}
              {selectedStagedFile && (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview: {selectedStagedFile}
                  </Typography>
                  
                  {!stagedFilePreview ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {/* Summary */}
                      <Stack direction="row" spacing={2}>
                        <Chip 
                          label={`${stagedFilePreview.summary.new} New`} 
                          color="success" 
                          size="small" 
                        />
                        <Chip 
                          label={`${stagedFilePreview.summary.updated} Updated`} 
                          color="warning" 
                          size="small" 
                        />
                        <Chip 
                          label={`${stagedFilePreview.summary.unchanged} Unchanged`} 
                          color="default" 
                          size="small" 
                        />
                      </Stack>

                      {/* New Items */}
                      {stagedFilePreview.diff.new.length > 0 && (
                        <Box>
                          <Typography variant="caption" fontWeight="bold" color="success.main">
                            NEW ({stagedFilePreview.diff.new.length})
                          </Typography>
                          <List dense>
                            {stagedFilePreview.diff.new.map(item => (
                              <ListItem key={item.id} sx={{ py: 0 }}>
                                <ListItemText 
                                  primary={item.displayName}
                                  secondary={`${item.questLineType} • ${item.questCount} quests, ${item.taskCount} tasks`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Updated Items */}
                      {stagedFilePreview.diff.updated.length > 0 && (
                        <Box>
                          <Typography variant="caption" fontWeight="bold" color="warning.main">
                            UPDATED ({stagedFilePreview.diff.updated.length})
                          </Typography>
                          <List dense>
                            {stagedFilePreview.diff.updated.map(item => (
                              <ListItem key={item.id} sx={{ py: 0 }}>
                                <ListItemText 
                                  primary={item.displayName}
                                  secondary={
                                    <Box>
                                      {item.changes.map((change, idx) => (
                                        <Typography key={idx} variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                                          {change.field}: {String(change.from)} → {String(change.to)}
                                        </Typography>
                                      ))}
                                    </Box>
                                  }
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Unchanged Items */}
                      {stagedFilePreview.diff.unchanged.length > 0 && (
                        <Box>
                          <Typography variant="caption" fontWeight="bold" color="text.secondary">
                            UNCHANGED ({stagedFilePreview.diff.unchanged.length})
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stagedFilePreview.diff.unchanged.map(item => item.displayName).join(', ')}
                          </Typography>
                        </Box>
                      )}

                      {/* Import Button */}
                      <Box display="flex" justifyContent="flex-end" gap={1} pt={1}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleImportStagedFile(selectedStagedFile)}
                          disabled={importingStagedFile}
                          startIcon={importingStagedFile ? <CircularProgress size={16} /> : <UploadIcon />}
                        >
                          {importingStagedFile ? 'Importing...' : 'Import Now'}
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setStagedFilesDialogOpen(false);
            setSelectedStagedFile(null);
            setStagedFilePreview(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}

